import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Info, ShieldAlert } from "lucide-react";
import { AppLayout } from "../../../components/layout";
import { ContentCard } from "../../../components/ui";
import { getCanonicalRoles } from "../../../lib/auth";
import sessionDB from "../../../utils/sessionDB";
import { getAuthenticatedEmployeeId } from "../../../lib/employee";
import {
  isApproverRole,
  isFinanceManagerRole,
  isReportingManagerRole,
} from "../constants/approvalConstants";
import { APPROVAL_API_READY } from "../../../services/conveyanceClaimService";


export function useApproverIdentity() {
  return useMemo(() => {
    const roles = getCanonicalRoles();
    const fromSession = Number(sessionDB.getEmpId());
    const viewerEmpId =
      Number.isFinite(fromSession) && fromSession > 0
        ? fromSession
        : getAuthenticatedEmployeeId();

    return {
      roles,
      isReportingManager: isReportingManagerRole(roles),
      isFinanceManager: isFinanceManagerRole(roles),
      isApprover: isApproverRole(roles),
      viewerEmpId,
    };
  }, []);
}


export function ApproverDeniedScreen({ requiredRole }) {
  const { t } = useTranslation();
  return (
    <AppLayout>
      <ContentCard bodyClassName="p-10 text-center">
        <div className="mx-auto mb-3 h-12 w-12 rounded-2xl bg-[var(--color-workspace)]/60 grid place-items-center text-[var(--color-text-secondary)]">
          <ShieldAlert className="h-6 w-6" />
        </div>
        <p className="text-sm font-bold text-foreground">
          {t("conveyanceApproval.noAccessTitle")}
        </p>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)] max-w-[460px] mx-auto">
          {t("conveyanceApproval.noAccessMessage", { role: requiredRole })}
        </p>
      </ContentCard>
    </AppLayout>
  );
}

/**
 * Why these screens are empty today.
 *
 * The approval endpoints are unpublished (see lib/approvalDataSource), so an
 * empty queue would otherwise be indistinguishable from a working screen with
 * no pending claims. The notice disappears on its own the moment
 * APPROVAL_API_READY flips.
 */
export function ApiPendingNotice({ className = "" }) {
  const { t } = useTranslation();
  if (APPROVAL_API_READY) return null;

  return (
    <div
      className={`mb-4 flex items-start gap-2.5 rounded-xl border border-[var(--color-accent)]/25 bg-[var(--color-accent)]/5 px-4 py-3 ${className}`}
    >
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
      <p className="text-[12px] leading-relaxed text-[var(--color-text-secondary)]">
        {t("conveyanceApproval.apiPendingNotice")}
      </p>
    </div>
  );
}
